import type { ReactNode } from 'react';
import type { BusSeatClassGlyph } from '../models.js';

/** Onde a planta está: `inline` deitada (a frente à esquerda) ou `sheet` em pé
 *  (a frente em cima). */
type GlyphLayout = 'inline' | 'sheet';

/** O comprimento da poltrona no sentido da viagem (a cama é mais longa) e a
 *  largura — unidades do `viewBox`, não pixels: o botão dá o tamanho. */
const SEAT_LENGTH = { seat: 40, bed: 52 } as const;
const SEAT_WIDTH = 40;

/**
 * A poltrona 2.5D vista de cima: o assento, o encosto atrás, os braços e uma
 * base mais escura que dá a sensação de altura. A cama (leito cama) é mais
 * longa e tem o apoio dos pés. Desenhada EM PÉ (frente em cima, encosto
 * embaixo); deitada, a matriz troca x e y: a frente vai para a esquerda e o
 * encosto para a direita — o ônibus anda para a esquerda no card. As cores
 * vêm das variáveis do botão (`--seat-*`), por estado. Decorativa: quem fala é
 * o botão.
 */
export function BusSeatGlyph({
  shape,
  layout,
}: {
  readonly shape: BusSeatClassGlyph;
  readonly layout: GlyphLayout;
}): ReactNode {
  const length = SEAT_LENGTH[shape];
  const lying = layout === 'inline';
  const width = lying ? length : SEAT_WIDTH;
  const height = lying ? SEAT_WIDTH : length;
  return (
    <svg
      className="hui-bus-seat-glyph"
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      aria-hidden="true"
      focusable="false"
    >
      <rect
        className="hui-bus-seat-glyph-depth"
        x={1}
        y={4}
        width={width - 2}
        height={height - 4}
        rx={9}
      />
      <g className="hui-bus-seat-glyph-lift">
        <g transform={lying ? 'matrix(0 1 1 0 0 0)' : undefined}>
          <rect
            className="hui-bus-seat-glyph-cushion"
            x={1}
            y={1}
            width={SEAT_WIDTH - 2}
            height={length - 5}
            rx={9}
          />
          <rect
            className="hui-bus-seat-glyph-part"
            x={5}
            y={length - 15}
            width={SEAT_WIDTH - 10}
            height={9}
            rx={4}
          />
          <rect
            className="hui-bus-seat-glyph-part"
            x={3}
            y={7}
            width={4}
            height={length - 24}
            rx={2}
          />
          <rect
            className="hui-bus-seat-glyph-part"
            x={SEAT_WIDTH - 7}
            y={7}
            width={4}
            height={length - 24}
            rx={2}
          />
          {shape === 'bed' && (
            <rect
              className="hui-bus-seat-glyph-part"
              x={10}
              y={4}
              width={SEAT_WIDTH - 20}
              height={5}
              rx={2.5}
            />
          )}
        </g>
      </g>
    </svg>
  );
}

/** Os rótulos da legenda da planta. */
export type BusSeatLegendCopy = {
  free: string;
  occupied: string;
  selected: string;
  recommended: string;
  window: string;
};

type LegendState = 'free' | 'occupied' | 'selected' | 'recommended';

/** Uma amostra da legenda: a mesma poltrona, no estado dela. */
function LegendSwatch({
  state,
  label,
}: {
  readonly state: LegendState;
  readonly label: string;
}): ReactNode {
  return (
    <span className="hui-bus-seat-legend-item">
      <span
        className="hui-bus-seat-swatch"
        data-state={state === 'recommended' ? 'free' : state}
        data-recommended={state === 'recommended' ? 'true' : undefined}
      >
        <BusSeatGlyph shape="seat" layout="sheet" />
      </span>
      {label}
    </span>
  );
}

/**
 * A legenda acima da planta: livre, ocupada, sua — e, só quando há, a
 * recomendada pelas Preferências e a janela (a janela só existe quando o
 * bus-api mandou as colunas).
 */
export function BusSeatLegend({
  copy,
  recommended,
  windows,
}: {
  readonly copy: BusSeatLegendCopy;
  readonly recommended: boolean;
  readonly windows: boolean;
}): ReactNode {
  return (
    <div className="hui-bus-seat-legend">
      <LegendSwatch state="free" label={copy.free} />
      <LegendSwatch state="occupied" label={copy.occupied} />
      <LegendSwatch state="selected" label={copy.selected} />
      {recommended && <LegendSwatch state="recommended" label={copy.recommended} />}
      {windows && (
        <span className="hui-bus-seat-legend-item">
          <span className="hui-bus-seat-legend-window" aria-hidden="true" />
          {copy.window}
        </span>
      )}
    </div>
  );
}
